import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CreditCardIcon,
  RepeatIcon,
  FileTextIcon,
  WalletIcon,
  ArrowLeftRightIcon,
  TerminalIcon,
  CheckCircle2Icon,
  ChevronRightIcon
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface ServiceItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets: string[];
  index: number;
}

function ServiceItem({
  icon,
  title,
  description,
  bullets,
  index
}: ServiceItemProps) {
  const { isDark } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cardRef.current) return;

    gsap.fromTo(
      cardRef.current,
      { opacity: 0, y: 40, scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top bottom-=80',
          toggleActions: 'play none none none',
          once: true
        },
        delay: index * 0.1
      }
    );
  }, [index]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || !glowRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    glowRef.current.style.background = `radial-gradient(circle at ${x}% ${y}%, rgba(6, 182, 212, 0.12), transparent 60%)`;
  };

  const handleMouseEnter = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1.1,
        rotate: 8,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  const handleMouseLeave = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1,
        rotate: 0,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  return (
    <motion.div
  className="relative h-full group"
  whileHover={{ y: -20 }}
  transition={{ type: 'spring', stiffness: 150, damping: 25 }} 
>
      {/* Outer cyan glow */}
      <div className="absolute -inset-[2px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-cyan-500/40 via-cyan-400/30 to-transparent"
           style={{ filter: 'blur(12px)' }} />

      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative h-full min-h-[20px] lg:min-h-[20px] flex flex-col p-8 lg:p-10 rounded-2xl border-2 transition-all duration-500 ${
          isDark
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 group-hover:border-cyan-500/60 shadow-xl'
            : 'bg-gradient-to-br from-white to-slate-50/80 border-slate-200/60 group-hover:border-cyan-400/80 shadow-xl'
        }`}
        style={{ backdropFilter: 'blur(8px)' }}
      >
        {/* Dynamic radial glow */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        />

        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Icon */}
        <div
          ref={iconRef}
          className={`relative w-16 h-16 flex items-center justify-center rounded-xl mb-8 transition-all duration-500 ${
            isDark
              ? 'bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500/20 shadow-lg shadow-cyan-500/20'
              : 'bg-gradient-to-br from-cyan-100 to-blue-100 text-cyan-600 group-hover:from-cyan-200 group-hover:to-blue-200 shadow-lg shadow-cyan-500/10'
          }`}
        >
          <div className="relative z-10">
            {icon}
          </div>

          {/* Icon glow */}
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-cyan-500/20"
               style={{ filter: 'blur(8px)' }} />
        </div>

        {/* Content Section */}
        <div className="flex-grow">
          <h3 className={`text-2xl font-bold mb-4 tracking-tight transition-colors duration-300 ${
            isDark ? 'text-white group-hover:text-cyan-400' : 'text-slate-900 group-hover:text-cyan-600'
          }`}>
            {title}
          </h3>
          <p className={`text-base leading-relaxed mb-8 ${
            isDark ? 'text-slate-400' : 'text-slate-600'
          }`}>
            {description}
          </p>

          {/* Bullet Points */}
          <ul className="space-y-3 mb-8">
            {bullets.map((bullet, i) => (
              <li
                key={i}
                className="flex items-center gap-3 group/item"
              >
                <div className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-lg transition-all duration-300 ${
                  isDark
                    ? 'bg-cyan-500/10 text-cyan-400 group-hover/item:bg-cyan-500/20'
                    : 'bg-cyan-100 text-cyan-600 group-hover/item:bg-cyan-200'
                }`}>
                  <CheckCircle2Icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-sm font-medium ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}>
                  {bullet}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Button */}
        <div href="#about" className={`mt-auto pt-6 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
          <button id="#premiumcta" className={`group/btn flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:translate-x-1 ${
            isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
          }`}>
            Explore Service
            <ChevronRightIcon className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
          </button>
        </div>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl overflow-hidden">
          <div className={`h-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out ${
            isDark
              ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500'
              : 'bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
          }`} />
        </div>
      </div>
    </motion.div>
  );
}

export function Services() {
  const { isDark } = useTheme();
  const badgeRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!badgeRef.current || !titleRef.current || !descRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: badgeRef.current,
        start: 'top 80%',
        toggleActions: 'play none none none',
        once: true
      }
    });

    tl.fromTo(badgeRef.current,
      { opacity: 0, y: -15 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
    )
    .fromTo(titleRef.current,
      { opacity: 0, y: 25 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
      '-=0.3'
    )
    .fromTo(descRef.current,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
      '-=0.4'
    );
  }, []);

  const servicesData = [
    {
      icon: <CreditCardIcon className="w-7 h-7" />,
      title: "Payment Processing",
      description: "Accept cards, wallets, bank transfers, and local payment methods across 150+ countries with a single API.",
      bullets: ['Cards & digital wallets', 'Bank transfers & ACH', 'Local payment methods']
    },
    {
      icon: <RepeatIcon className="w-7 h-7" />,
      title: "Subscription Billing",
      description: "Flexible recurring billing with smart dunning, proration, usage-based pricing, and automated invoicing.",
      bullets: ['Smart dunning & retries', 'Usage-based pricing', 'Proration handling']
    },
    {
      icon: <FileTextIcon className="w-7 h-7" />,
      title: "Invoicing",
      description: "Professional invoices with payment links, automated reminders, and multi-currency support built in.",
      bullets: ['Payment links', 'Auto reminders', 'Multi-currency']
    },
    {
      icon: <WalletIcon className="w-7 h-7" />,
      title: "Virtual Cards",
      description: "Issue virtual and physical cards for your team. Set spending limits, categories, and real-time controls.",
      bullets: ['Instant issuance', 'Spending controls', 'Real-time alerts']
    },
    {
      icon: <ArrowLeftRightIcon className="w-7 h-7" />,
      title: "Cross-Border Payments",
      description: "Send and receive international payments with competitive FX rates and full regulatory compliance.",
      bullets: ['Competitive FX rates', '150+ countries', 'Full compliance']
    },
    {
      icon: <TerminalIcon className="w-7 h-7" />,
      title: "Developer Tools",
      description: "Comprehensive SDKs, CLI tools, webhooks, and sandbox environments for rapid integration and testing.",
      bullets: ['SDKs for all languages', 'Webhook management', 'Sandbox testing']
    }
  ];

  return (
    <section id="services" className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center pb-16 lg:pb-20">
          <div>
            <div
              ref={badgeRef}
              className={`inline-flex items-center gap-2 px-4 rounded-full text-xs font-bold tracking-[0.2em] uppercase mb-6 border-2 ${
                isDark ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' : 'bg-cyan-100 text-cyan-600 border-cyan-300'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
              Our Expertise
            </div>

            <h2
              ref={titleRef}
              className={`text-4xl md:text-5xl mb-6 lg:text-6xl font-bold tracking-tight leading-[1.1] ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              Complete{' '}
              <span className={`relative inline-block ${
                isDark
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600'
              }`}>
                payment solutions
              </span>
            </h2>
          </div>

          <p
            ref={descRef}
            className={`text-lg lg:text-xl text-center leading-relaxed ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            From checkout to payout, we cover every step of the payment
            lifecycle with{' '}
            <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
              enterprise-grade infrastructure
            </span>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-20">
          {servicesData.map((service, index) => (
            <ServiceItem
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              bullets={service.bullets}
              index={index}
            />
          ))}
        </div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className={`relative p-10 lg:p-12 rounded-2xl border-2 text-center overflow-hidden ${
            isDark
              ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50'
              : 'bg-gradient-to-br from-white to-slate-50/80 border-slate-200/60'
          }`}
          style={{ backdropFilter: 'blur(8px)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

          <div className="relative z-10">
            <h4 className={`text-2xl lg:text-3xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              Ready to transform your payment infrastructure?
            </h4>
            <p className={`mb-8 max-w-xl mx-auto text-base lg:text-lg ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Join thousands of forward-thinking companies scaling their business with our professional services.
            </p>
            <a
              href="#top"
              className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:scale-105 active:scale-95 border-2 ${
                isDark
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 border-cyan-400/50 text-white shadow-xl shadow-cyan-500/40'
                  : 'bg-gradient-to-r from-cyan-600 to-blue-600 border-cyan-500/50 text-white shadow-xl shadow-cyan-600/40'
              }`}
            >
              Get Started Now
              <ChevronRightIcon className="w-5 h-5" />
            </a>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}